/**
 * The item in your right hand (Horizon-style placement).
 *
 * Pick a shape, model, light or copy on the wrist palette and it appears out
 * in front of your right hand, at its real size, as a see-through preview. Move
 * your hand to where it should go and release — pinch (hands) or trigger
 * (controller) — to drop it there. B / Y on a controller, or Cancel on the
 * palette, puts it away.
 *
 * Hands read the shared pinch filter (hysteresis + 120 ms grace), so a
 * tracking blip can't drop the item early, and the pinch that drops it is
 * consumed — it can't also grab whatever is behind the item that frame.
 *
 * Snap (palette ▸ Edit) rounds the drop point to the grid and the turn to
 * 15°. Game tables and portals always stand on the floor; everything else is
 * kept from sinking below it.
 */
import React, { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { useWorldStore } from '../../../stores/worldStore';
import { hands, handMode, pinch } from '../hands/handTracking';
import { noRaycast } from '../vrUi';
import { useVRBuild, type HeldItem } from './vrBuildState';
import { newPrimitiveItem, placeContent, placeLight, placePrimitive } from './vrPlace';
import { usePortalDraft, newPortalLink, PORTAL_W, PORTAL_H } from '../../portals/portals';
import { GT_W, GT_H, GT_REST_Y } from '../../games/tableLayout';
import { getGameDef } from '../../games/registry';
import { ARC_W, ARC_H, ARC_D, ARC_REST_Y, DEFAULT_BOARD, boardBox, boardRestY } from '../../games/arcade/arcadeShared';
import { SPK_W, SPK_H, SPK_D } from '../../music/SpeakerModel3D';

type V3 = [number, number, number];
const ROT_STEP = Math.PI / 12; // 15°

/** Half-height and reach radius of what's held, at its real scale. */
function sizeOf(h: HeldItem): { half: number; radius: number; box: V3 } {
  if (h.kind === 'light') return { half: .1, radius: .12, box: [.2, .2, .2] };
  if (h.kind === 'content') {
    const s = typeof h.template?.scale === 'number' ? h.template.scale : 1;
    return { half: .4 * s, radius: .45 * s, box: [.8 * s, .8 * s, .8 * s] };
  }
  const sc = h.template?.scale ?? [1, 1, 1];
  if (h.shape === 'gametable') {
    const fp = getGameDef(h.game ?? h.template?.game)?.footprint;
    const w = fp?.w ?? GT_W, d = fp?.d ?? GT_W;
    return { half: GT_H / 2, radius: Math.max(w, d) / 2, box: [w, GT_H, d] };
  }
  if (h.shape === 'portal') return { half: PORTAL_H / 2, radius: PORTAL_W / 2, box: [PORTAL_W, PORTAL_H, PORTAL_W] };
  if (h.shape === 'arcade') return { half: ARC_H / 2, radius: Math.max(ARC_W, ARC_D) / 2, box: [ARC_W, ARC_H, ARC_D] };
  if (h.shape === 'speaker') return { half: SPK_H / 2, radius: Math.max(SPK_W, SPK_D) / 2, box: [SPK_W, SPK_H, SPK_D] };
  if (h.shape === 'leaderboard') { const b = boardBox({ ...DEFAULT_BOARD, ...(h.board ?? h.template?.board ?? {}) }); return { half: b[1] / 2, radius: Math.max(b[0], b[2]) / 2, box: b }; }
  const flat = h.shape === 'plane';
  return { half: flat ? .02 : .5 * sc[1], radius: .5 * Math.max(sc[0], sc[1], sc[2]), box: [sc[0], flat ? .02 : sc[1], sc[2]] };
}

function GhostGeometry({ held }: { held: HeldItem }) {
  if (held.kind === 'light') return <sphereGeometry args={[.1, 16, 12]} />;
  if (held.kind === 'content') return <boxGeometry args={[.8, .8, .8]} />;
  const sc = held.template?.scale ?? [1, 1, 1];
  switch (held.shape) {
    case 'sphere': return <sphereGeometry args={[.5 * sc[0], 24, 16]} />;
    case 'cylinder': return <cylinderGeometry args={[.5 * sc[0], .5 * sc[0], sc[1], 24]} />;
    case 'cone': return <coneGeometry args={[.5 * sc[0], sc[1], 24]} />;
    case 'torus': return <torusGeometry args={[.4 * sc[0], .15 * sc[0], 10, 24]} />;
    case 'plane': return <boxGeometry args={[sc[0], .02, sc[2] ?? sc[1]]} />;
    default: { const b = sizeOf(held).box; return <boxGeometry args={b} />; }
  }
}

export default function VRHeldItem({ worldId }: { worldId: string }) {
  const held = useVRBuild((s) => s.held);
  const canBuild = useWorldStore((s) => s.canBuild);
  const origin = useXR((s) => s.origin);
  const session = useXR((s) => s.session);
  const { camera, gl } = useThree();
  const ghost = useRef<THREE.Group>(null);
  // The press that picked the item is still down: wait for it to be released.
  const armed = useRef(false);
  const cancelPrev = useRef(true);
  const last = useRef<{ pos: V3; rotY: number } | null>(null);
  const t = useMemo(() => ({ p: new THREE.Vector3(), d: new THREE.Vector3(), e: new THREE.Vector3(), q: new THREE.Quaternion(), m: new THREE.Matrix4() }), []);
  const heldKey = useRef<HeldItem | null>(null);

  const color = held?.kind === 'primitive' ? (held.template?.color ?? (held.shape === 'portal' ? '#8b5cf6' : held.shape === 'gametable' ? '#0f5a3c' : held.shape === 'arcade' ? '#00c2b3' : '#9fdcd6'))
    : held?.kind === 'light' ? '#ffe9a8' : '#9fdcd6';

  const drop = () => {
    const h = useVRBuild.getState().held; const l = last.current;
    if (!h || !l) return;
    const store = useWorldStore.getState();
    if (h.kind === 'light') { placeLight(worldId, l.pos, h.template); useVRBuild.getState().setHeld(null); return; }
    if (h.kind === 'content') {
      placeContent(worldId, h.spec, [l.pos[0], l.pos[1] - sizeOf(h).half, l.pos[2]], l.rotY, h.template);
      useVRBuild.getState().setHeld(null);
      return;
    }
    // Shapes (incl. game tables and portals).
    if (h.shape === 'portal' && !h.template) {
      const draft = usePortalDraft.getState();
      const link = draft.pending?.link ?? newPortalLink();
      const item = newPrimitiveItem('portal', l.pos, l.rotY, { portalLink: link, name: draft.pending ? 'Portal (exit)' : 'Portal (entrance)' });
      const id = placePrimitive(worldId, item);
      if (!draft.pending) { draft.set({ link, id }); armed.current = false; return; } // keep holding: now place the exit
      draft.set(null);
      useVRBuild.getState().setHeld(null);
      return;
    }
    const tpl = h.template;
    const item = tpl
      ? { ...tpl, position: l.pos, rotation: [tpl.rotation?.[0] ?? 0, l.rotY, tpl.rotation?.[2] ?? 0] as V3,
          name: tpl.name?.endsWith('Copy') ? tpl.name : `${tpl.name} Copy`, ...(tpl.shape === 'portal' ? { portalLink: undefined } : {}) }
      : newPrimitiveItem(h.shape, l.pos, l.rotY, h.game ? { game: h.game } : h.arcadeGame ? { arcadeGame: h.arcadeGame } : h.shape === 'leaderboard' ? { board: { ...DEFAULT_BOARD, ...(h.board ?? {}) } } : {});
    placePrimitive(worldId, item);
    void store;
    useVRBuild.getState().setHeld(null);
  };

  useFrame((_s, _dt, frame?: XRFrame) => {
    const g = ghost.current;
    const h = useVRBuild.getState().held;
    if (h !== heldKey.current) { heldKey.current = h; armed.current = false; cancelPrev.current = true; }
    if (!g || !h || !origin || !session) { if (g) g.visible = false; return; }
    origin.updateMatrixWorld();

    // ---- Where is the right hand? ----
    let found = false; let pressed = false; let cancel = false; let isHand = false;
    let pad: Gamepad | undefined;
    if (handMode.active && hands.right.present) {
      const r = hands.right;
      t.p.copy(r.indexTip).add(r.thumbTip).multiplyScalar(.5).applyMatrix4(origin.matrixWorld);
      t.d.copy(r.rayDir).transformDirection(origin.matrixWorld);
      pressed = pinch.right.held;
      isHand = true;
      found = true;
    } else if (frame) {
      const ref = gl.xr.getReferenceSpace();
      for (const src of session.inputSources) {
        if (src.handedness !== 'right' || src.hand || !src.gripSpace || !ref) continue;
        const pose = frame.getPose(src.gripSpace, ref);
        if (!pose) continue;
        t.m.fromArray(pose.transform.matrix).premultiply(origin.matrixWorld);
        t.p.setFromMatrixPosition(t.m);
        t.q.setFromRotationMatrix(t.m);
        t.d.set(0, 0, -1).applyQuaternion(t.q);
        pad = src.gamepad ?? undefined;
        pressed = !!pad?.buttons?.[0]?.pressed;
        cancel = !!pad?.buttons?.[5]?.pressed;
        found = true;
        break;
      }
    }
    if (!found) { g.visible = false; return; }

    // ---- Pose the preview in front of the hand ----
    const { half, radius } = sizeOf(h);
    const snap = useWorldStore.getState().snapSettings;
    // Out in front of the hand, far enough that you see the whole thing and
    // your hand never ends up inside it (a 1 m box floats ~1.2 m out).
    const pos = t.p.clone().addScaledVector(t.d, .3 + radius * 1.8);
    let rotY = Math.atan2(t.d.x, t.d.z) + Math.PI; // the face you'd see looks back at you
    rotY = Math.atan2(Math.sin(rotY), Math.cos(rotY)); // keep it in -π..π
    const shape = h.kind === 'primitive' ? h.shape : null;
    if (shape === 'portal') { camera.getWorldPosition(t.e); rotY = Math.atan2(t.e.x - pos.x, t.e.z - pos.z); }
    if (snap.grid) {
      const gs = snap.gridSize || .5;
      pos.x = Math.round(pos.x / gs) * gs; pos.z = Math.round(pos.z / gs) * gs;
      if (shape !== 'portal' && shape !== 'gametable' && shape !== 'arcade' && shape !== 'leaderboard') pos.y = Math.round(pos.y / gs) * gs;
      rotY = Math.round(rotY / ROT_STEP) * ROT_STEP;
    }
    if (shape === 'gametable') pos.y = GT_REST_Y;
    else if (shape === 'arcade') pos.y = ARC_REST_Y;
    else if (shape === 'leaderboard') { const c = { ...DEFAULT_BOARD, ...((h.kind === 'primitive' && (h.board ?? h.template?.board)) || {}) }; if (c.mount === 'stand') pos.y = boardRestY(c); }
    else if (shape === 'portal') pos.y = PORTAL_H / 2;
    else if (pos.y < half) pos.y = half; // never sink into the floor
    g.position.copy(pos);
    g.rotation.set(0, rotY, 0);
    g.visible = true;
    last.current = { pos: [+pos.x.toFixed(3), +pos.y.toFixed(3), +pos.z.toFixed(3)], rotY };

    // ---- Release to drop (after the picking press has been let go) ----
    if (!pressed) armed.current = true;
    else if (armed.current) {
      armed.current = false;
      // This pinch is spent: nothing else (a grab behind the item) may start with it.
      if (isHand) pinch.right.consume();
      drop();
      try { (pad as any)?.hapticActuators?.[0]?.pulse?.(.4, 40); } catch { /* no haptics */ }
    }
    if (cancel && !cancelPrev.current) { useVRBuild.getState().setHeld(null); usePortalDraft.getState().set(null); }
    cancelPrev.current = cancel;
  });

  if (!session || !canBuild || !held) return null;
  return <group ref={ghost} visible={false} name="vr-held-item">
    <mesh raycast={noRaycast} renderOrder={5}>
      <GhostGeometry held={held} />
      <meshBasicMaterial color={color} transparent opacity={.38} depthWrite={false} toneMapped={false} />
    </mesh>
    <mesh raycast={noRaycast} renderOrder={6}>
      <GhostGeometry held={held} />
      <meshBasicMaterial color="#5ee6dc" wireframe transparent opacity={.55} toneMapped={false} />
    </mesh>
    {/* a floor marker under it, so you can see where it will stand */}
    <mesh raycast={noRaycast} position={[0, -sizeOf(held).half + .005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[Math.max(.08, sizeOf(held).radius * .9), Math.max(.1, sizeOf(held).radius), 32]} />
      <meshBasicMaterial color="#5ee6dc" transparent opacity={.45} toneMapped={false} side={THREE.DoubleSide} />
    </mesh>
  </group>;
}
